import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, FileUp, Sparkles, Loader2, Check, Trash2, Download, FileText, Plus, Puzzle, ChevronDown, ChevronUp } from "lucide-react";
import CostingSection from "@/components/CostingSection";
import RFQWorkflow from "@/components/RFQWorkflow";
import { toast } from "sonner";

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const fmtBytes = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const QUOTE_CURRENCIES = ["USD", "ZAR", "EUR", "GBP", "AUD", "AED", "INR", "CNY", "KES", "TZS", "JPY", "CHF"];
const INPUT_YELLOW = "bg-[#FFFBEB] border-[#EAD9A0] focus-visible:ring-[#E0B84B]";

// One wizard step: expanded (active) shows full content; collapsed shows a summary row
// (green when done, neutral otherwise). Clicking a collapsed row opens that step.
function StepShell({ n, title, subtitle, active, done, summary, onOpen, onToggle, children }) {
  if (!active) {
    return (
      <button
        type="button"
        onClick={onOpen}
        data-testid={`step-summary-${n}`}
        className={`flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left transition-colors ${
          done ? "border-[#527D63]/30 bg-[#E4EEE8]/70 hover:bg-[#E4EEE8]" : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${done ? "bg-[#527D63] text-white" : "bg-slate-100 text-slate-500"}`}>
            {done ? <Check className="h-4 w-4" /> : n}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-800">{title}</span>
            <span className="block truncate text-xs text-muted-foreground">{done ? summary : subtitle}</span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-[#1B4383]">Open<ChevronDown className="h-4 w-4" /></span>
      </button>
    );
  }
  return (
    <div data-testid={`step-panel-${n}`} className="overflow-hidden rounded-lg border border-[#1B4383]/25 bg-white shadow-sm ring-1 ring-[#1B4383]/5">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`step-toggle-${n}`}
        className="flex w-full items-center gap-3 border-b border-slate-200 bg-[#F3F6FB] px-5 py-4 text-left transition-colors hover:bg-[#EAF0F9]"
      >
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${done ? "bg-[#527D63] text-white" : "bg-[#1B4383] text-white"}`}>
          {done ? <Check className="h-4 w-4" /> : n}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading text-base font-bold tracking-tight text-slate-900">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-slate-500">Collapse<ChevronUp className="h-5 w-5" /></span>
      </button>
      <div className="space-y-6 p-5">{children}</div>
    </div>
  );
}

function WizardNav({ onBack, onNext, nextLabel, nextTestId, nextLoading }) {
  return (
    <div className="flex items-center justify-between border-t border-slate-200 pt-4">
      {onBack ? (
        <Button variant="outline" onClick={onBack} data-testid="wizard-back"><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
      ) : <span />}
      {onNext && (
        <Button onClick={onNext} disabled={nextLoading} data-testid={nextTestId} className="bg-[#1B4383] hover:bg-[#122E5C]">
          {nextLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{nextLabel}<ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default function SupplierQuotePage() {
  const { id, supplierId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ currency: "", payment_terms: "", validity: "", notes: "" });
  const [lines, setLines] = useState([]);
  const [extras, setExtras] = useState([]);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [docs, setDocs] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matching, setMatching] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revId, setRevId] = useState(null);
  const [revLabel, setRevLabel] = useState("");
  const [creatingRev, setCreatingRev] = useState(false);
  const [clearDocs, setClearDocs] = useState(false);
  const [costingDone, setCostingDone] = useState(false);
  const [costingReloadKey, setCostingReloadKey] = useState(0);
  const [open, setOpen] = useState({ 1: false, 2: false, 3: false });
  const isOpen = (n) => !!open[n];
  const openStep = (n) => setOpen((o) => ({ ...o, [n]: true }));
  const closeStep = (n) => setOpen((o) => ({ ...o, [n]: false }));
  const advance = (from, to) => setOpen((o) => ({ ...o, [from]: false, [to]: true }));
  const [quoteLoaded, setQuoteLoaded] = useState(false);
  const didInit = useRef(false);

  const loadFiles = useCallback(() => {
    api.get(`/rfqs/${id}/suppliers/${supplierId}/quote-files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [id, supplierId]);

  const loadQuote = useCallback(() => {
    api.get(`/rfqs/${id}/suppliers/${supplierId}/quote`).then(({ data }) => {
      setMeta({ currency: data.currency || "", payment_terms: data.payment_terms || "", validity: data.validity || "", notes: data.notes || "" });
      setLines(data.lines.map((l) => ({ item_id: l.item_id, description: l.description, part_number: l.spec_or_part || "", unit_price: l.unit_price ?? "", disc_pct: l.disc_pct || "", lead_time: l.lead_time || "", quoted_qty: l.quoted_qty ?? "" })));
      setRevisions(data.revisions || []);
      setRevId(data.revision);
      setRevLabel(data.label || "");
      setQuoteLoaded(true);
    }).catch((e) => toast.error(apiError(e)));
  }, [id, supplierId]);

  useEffect(() => {
    api.get(`/rfqs/${id}`).then((r) => setRfq(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/suppliers/${supplierId}`).then((r) => setSupplierName(r.data.name)).catch(() => {});
    api.get(`/rfqs/${id}/items`).then((r) => setItems(r.data)).catch(() => {});
    loadQuote();
    api.get(`/rfqs/${id}/suppliers/${supplierId}/quote-extras`).then((r) => setExtras(r.data.map((e) => ({
      id: e.id, kind: e.kind, parent_item_id: e.parent_item_id || "", description: e.description || "",
      spec_or_part: e.spec_or_part || "", quantity: e.quantity ?? "", unit: e.unit || "",
      unit_price: e.unit_price ?? "", lead_time: e.lead_time || "", optional: !!e.optional,
    })))).catch(() => {});
    loadFiles();
  }, [id, supplierId, loadFiles, loadQuote]);

  // Land the user on the step they should act on next (only once, after data loads).
  useEffect(() => {
    if (didInit.current || !quoteLoaded) return;
    didInit.current = true;
    const cap = files.length > 0 || lines.some((l) => l.unit_price !== "" && l.unit_price != null) || !!meta.currency;
    const mapped = lines.filter((l) => l.item_id);
    const priced = mapped.length > 0 && mapped.every((l) => l.unit_price !== "" && l.unit_price != null);
    const first = !cap ? 1 : !(meta.currency && priced) ? 2 : 3;
    setOpen({ 1: first === 1, 2: first === 2, 3: first === 3 });
  }, [quoteLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistQuote = async () => {
    const valid = lines.filter((l) => l.item_id);
    await api.post(`/rfqs/${id}/suppliers/${supplierId}/quote`, {
      ...meta,
      lines: valid.map(linePayload),
      draft_lines: lines.map(draftPayload),
    });
    setCostingReloadKey((k) => k + 1);
  };

  // Auto-save Step 2 line prices/meta so nothing is lost when navigating away.
  const autoSaveReady = useRef(false);
  useEffect(() => {
    if (!quoteLoaded) return;
    if (!autoSaveReady.current) { autoSaveReady.current = true; return; }
    const t = setTimeout(() => { persistQuote().catch(() => {}); }, 1200);
    return () => clearTimeout(t);
  }, [lines, meta, quoteLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchVersion = async (n) => {
    if (Number(n) === revId) return;
    try {
      await api.post(`/rfqs/${id}/suppliers/${supplierId}/costing/revisions/${n}/activate`);
      loadQuote();
      toast.success(`Viewing Rev ${n}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addRevision = async () => {
    setCreatingRev(true);
    try {
      await persistQuote();
      const { data } = await api.post(`/rfqs/${id}/suppliers/${supplierId}/costing/revisions`, { label: "", clear_documents: clearDocs });
      setText("");
      setDocs([]);
      if (clearDocs) loadFiles();
      loadQuote();
      setOpen({ 1: true, 2: false, 3: false });
      toast.success(`Created Rev ${data.revision} — a fresh blank revision, enter the supplier's new pricing`);
    } catch (e) { toast.error(apiError(e)); } finally { setCreatingRev(false); }
  };

  const deleteVersion = async () => {
    if (!revId) return;
    if (!window.confirm(`Delete Rev ${revId}? This cannot be undone.`)) return;
    try {
      await api.delete(`/rfqs/${id}/suppliers/${supplierId}/costing/revisions/${revId}`);
      toast.success(`Rev ${revId} deleted`);
      loadQuote();
    } catch (e) { toast.error(apiError(e)); }
  };

  const storeFile = async (f, kind = "quote") => {
    const b64 = await readFileBase64(f);
    await api.post(`/rfqs/${id}/suppliers/${supplierId}/quote-files`, { filename: f.name, content_type: f.type, file_base64: b64, kind });
    loadFiles();
  };

  const docTypeOf = (f) => {
    const n = (f.name || "").toLowerCase();
    if (f.type === "application/pdf" || n.endsWith(".pdf")) return "pdf";
    if (n.endsWith(".xlsx") || n.endsWith(".xls") || (f.type || "").includes("sheet") || (f.type || "").includes("excel")) return "excel";
    if ((f.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|heic)$/.test(n)) return "image";
    return null;
  };

  const addDocs = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    incoming.forEach((f) => {
      if (!docTypeOf(f)) { toast.error(`Unsupported file: ${f.name}`); return; }
      accepted.push(f);
    });
    if (accepted.length) setDocs((d) => [...d, ...accepted]);
  };

  const removeDoc = (idx) => setDocs((d) => d.filter((_, i) => i !== idx));

  const parse = async () => {
    const hasText = text.trim().length > 0;
    if (!hasText && !docs.length) { toast.error("Paste quote text or add at least one document."); return; }
    setParsing(true);
    try {
      const documents = [];
      if (hasText) documents.push({ source_type: "text", text });
      for (const f of docs) {
        const b64 = await readFileBase64(f);
        documents.push({ source_type: docTypeOf(f), file_base64: b64, filename: f.name });
      }
      const { data } = await api.post(`/rfqs/${id}/suppliers/${supplierId}/parse-quote-multi`, { documents });
      setMeta((m) => ({ currency: data.currency || m.currency, payment_terms: data.payment_terms || m.payment_terms, validity: data.validity || m.validity, notes: m.notes }));
      setLines(data.lines.map((l) => ({ item_id: l.suggested_item_id || "", description: l.description, part_number: l.part_number || "", unit_price: l.unit_price ?? "", disc_pct: l.discount_pct ?? "", lead_time: l.lead_time || "", quoted_qty: l.quantity ?? "" })));
      setItems(data.items.map((i) => ({ id: i.id, description: i.description, spec_or_part: i.spec_or_part, quantity: i.quantity, unit: i.unit })));
      for (const f of docs) { try { await storeFile(f); } catch { /* keep parse result even if store fails */ } }
      setDocs([]);
      toast.success(`Read ${data.lines.length} line(s) from ${documents.length} document(s) — review the matches in Step 2`);
    } catch (e) { toast.error(apiError(e)); } finally { setParsing(false); }
  };

  const uploadOnly = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try { for (const f of list) { await storeFile(f); } toast.success(list.length > 1 ? `${list.length} documents saved` : "Quote document saved"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const uploadDatasheets = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try { for (const f of list) { await storeFile(f, "datasheet"); } toast.success(list.length > 1 ? `${list.length} datasheets saved` : "Datasheet saved"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm("Delete this quote document?")) return;
    try { await api.delete(`/rfqs/${id}/suppliers/${supplierId}/quote-files/${fileId}`); loadFiles(); toast.success("Deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/rfqs/${id}/suppliers/${supplierId}/quote-files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = f.filename || "quote";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Download failed"); }
  };

  const save = async () => {
    const valid = lines.filter((l) => l.item_id);
    const extraRows = extras.filter((e) => (e.description && e.description.trim()) || e.unit_price !== "");
    if (!valid.length && !extraRows.length) { toast.error("Map a line item or add an extra line first."); return; }
    const badAlt = extraRows.find((e) => e.kind === "alternative" && !e.parent_item_id);
    if (badAlt) { toast.error("Each alternative must say which line item it replaces."); return; }
    setSaving(true);
    try {
      await api.post(`/rfqs/${id}/suppliers/${supplierId}/quote`, {
        ...meta,
        lines: valid.map(linePayload),
        draft_lines: lines.map(draftPayload),
      });
      await api.put(`/rfqs/${id}/suppliers/${supplierId}/quote-extras`, {
        extras: extraRows.map((e) => ({
          id: e.id, kind: e.kind,
          parent_item_id: e.parent_item_id || null,
          description: e.description, spec_or_part: e.spec_or_part,
          quantity: e.quantity === "" ? null : Number(e.quantity), unit: e.unit,
          unit_price: e.unit_price === "" ? null : Number(e.unit_price),
          lead_time: e.lead_time, optional: !!e.optional,
        })),
      });
      toast.success("Quote saved");
      navigate(`/rfqs/${id}?tab=quotes`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const goToCosting = async () => {
    setAdvancing(true);
    try { await persistQuote(); } catch (e) { toast.error(apiError(e)); }
    finally { setAdvancing(false); advance(2, 3); }
  };

  const addExtra = (kind) => setExtras((p) => [...p, { kind, parent_item_id: "", description: "", spec_or_part: "", quantity: "", unit: "", unit_price: "", lead_time: "", optional: false }]);
  const setExtra = (i, patch) => setExtras((p) => p.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const removeExtra = (i) => setExtras((p) => p.filter((_, idx) => idx !== i));
  const extraTotal = (e) => {
    const q = e.quantity === "" ? null : Number(e.quantity);
    const pr = e.unit_price === "" ? null : Number(e.unit_price);
    if (q == null || pr == null || Number.isNaN(q) || Number.isNaN(pr)) return null;
    return q * pr;
  };

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const listOf = (l) => (l.unit_price === "" || l.unit_price == null ? null : Number(l.unit_price));
  const netOf = (l) => { const p = listOf(l); const d = Math.min(100, Math.max(0, Number(l.disc_pct) || 0)); return p == null ? null : Math.round(p * (1 - d / 100) * 10000) / 10000; };
  const discOf = (l) => Math.min(100, Math.max(0, Number(l.disc_pct) || 0));
  const qqOut = (l) => (l.quoted_qty === "" || l.quoted_qty == null || Number(l.quoted_qty) === itemQty(l.item_id) ? null : Number(l.quoted_qty));
  const linePayload = (l) => ({ item_id: l.item_id, unit_price: netOf(l), list_price: listOf(l), disc_pct: discOf(l), lead_time: l.lead_time, description: l.description || "", quoted_qty: qqOut(l) });
  const draftPayload = (l) => ({ item_id: l.item_id || "", description: l.description || "", part_number: l.part_number || "", unit_price: netOf(l), list_price: listOf(l), disc_pct: discOf(l), lead_time: l.lead_time || "", quoted_qty: l.quoted_qty === "" || l.quoted_qty == null ? null : Number(l.quoted_qty) });
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const autoMatch = async () => {
    if (!lines.length) return;
    setMatching(true);
    try {
      const { data } = await api.post(`/rfqs/${id}/suppliers/${supplierId}/match-lines`, {
        lines: lines.map((l) => ({ description: l.description || "", part_number: l.part_number || "" })),
      });
      const sugg = data.suggested_item_ids || [];
      const toFill = lines.map((l, i) => (!l.item_id && sugg[i] ? i : -1)).filter((x) => x >= 0);
      if (toFill.length) setLines((prev) => prev.map((l, i) => (toFill.includes(i) ? { ...l, item_id: sugg[i] } : l)));
      toast.success(toFill.length ? `Auto-matched ${toFill.length} line${toFill.length === 1 ? "" : "s"}` : "No new matches found");
    } catch (e) { toast.error(apiError(e)); } finally { setMatching(false); }
  };
  const itemQty = (iid) => { const it = items.find((x) => x.id === iid); return it && it.quantity != null ? it.quantity : null; };
  const itemUnit = (iid) => { const it = items.find((x) => x.id === iid); return it ? (it.unit || "") : ""; };
  const qtyOf = (l) => (l.quoted_qty !== "" && l.quoted_qty != null && !Number.isNaN(Number(l.quoted_qty)) ? Number(l.quoted_qty) : itemQty(l.item_id));
  const lineTotal = (l) => {
    const q = qtyOf(l);
    const p = netOf(l);
    if (q == null || p == null || Number.isNaN(p)) return null;
    return q * p;
  };
  const grossTotal = (l) => { const q = qtyOf(l); const p = listOf(l); return q == null || p == null ? 0 : q * p; };
  const qtyDiff = (l) => l.item_id && l.quoted_qty !== "" && l.quoted_qty != null && itemQty(l.item_id) != null && Number(l.quoted_qty) !== itemQty(l.item_id);
  const QtyInput = ({ l, i, m = "" }) => (
    <Input inputMode="decimal" value={l.quoted_qty ?? ""} placeholder={itemQty(l.item_id) != null ? String(itemQty(l.item_id)) : ""} onChange={(e) => setLine(i, { quoted_qty: e.target.value.replace(/[^0-9.]/g, "") })}
      className={`h-9 text-right font-mono ${qtyDiff(l) ? "border-amber-400 bg-amber-50 text-amber-900" : ""}`} title={qtyDiff(l) ? `Supplier quoted ${l.quoted_qty}, you asked for ${itemQty(l.item_id)}` : "Quantity the supplier quoted"} data-testid={`quote-qty${m}-${i}`} />
  );
  const applyDiscAll = (v) => setLines((prev) => prev.map((l) => ({ ...l, disc_pct: v === "" ? "" : String(Math.min(100, Number(v) || 0)) })));
  const fmtTotal = (v) => (v == null ? "—" : `${meta.currency ? meta.currency + " " : ""}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const withCommas = (v) => {
    if (v === "" || v == null) return "";
    const parts = String(v).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
  };
  const mappedCount = lines.filter((l) => l.item_id).length;
  const matchedSum = lines.reduce((s, l) => s + (lineTotal(l) || 0), 0);
  const grossSum = lines.reduce((s, l) => s + grossTotal(l), 0);
  const discSum = grossSum - matchedSum;
  const addonSum = extras.filter((e) => e.kind === "addon").reduce((s, e) => s + (extraTotal(e) || 0), 0);
  const altCount = extras.filter((e) => e.kind === "alternative").length;
  const baseTotal = matchedSum + addonSum;

  const mappedLines = lines.filter((l) => l.item_id);
  const linesPriced = mappedLines.length > 0 && mappedLines.every((l) => l.unit_price !== "" && l.unit_price != null);
  const captureDone = files.length > 0 || lines.some((l) => l.unit_price !== "" && l.unit_price != null) || !!meta.currency;
  const verifyDone = !!meta.currency && linesPriced;
  const step1Summary = `${files.length} document(s) attached${lines.length ? ` · ${lines.length} line(s) read` : ""}`;
  const step2Summary = `${meta.currency || "No currency"} · ${mappedCount} of ${lines.length} line(s) priced · ${fmtTotal(baseTotal)}`;
  const step3Summary = costingDone ? "Basis Confirmed for the Somcore Quote" : "Costing built";
  const wfSteps = [
    { key: "capture", label: "Capture Quote", done: captureDone, onClick: () => openStep(1) },
    { key: "verify", label: "Verify Prices", done: verifyDone, onClick: () => openStep(2) },
    { key: "costing", label: "Build Costing", done: costingDone, onClick: () => openStep(3) },
  ];
  const wfCurrent = wfSteps.findIndex((s) => !s.done);

  return (
    <AppLayout title="Supplier Quote">
      <button onClick={() => navigate(`/rfqs/${id}?tab=quotes`)} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-rfq">
        <ArrowLeft className="h-4 w-4" /> Back to RFQ
      </button>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-white p-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-lg font-bold tracking-tight text-slate-900" data-testid="sq-supplier-name">{supplierName || "Supplier"}</span>
            {revId && <span className="rounded-full bg-[#E6ECF5] px-2 py-0.5 text-xs font-semibold text-[#1B4383]" data-testid="sq-revision-badge">Rev {revId}{revLabel ? ` · ${revLabel}` : ""}</span>}
          </div>
          <div className="text-sm text-muted-foreground">Quote for RFQ <span className="font-mono text-[#1B4383]">{rfq?.code || rfq?.title || ""}</span></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">Version</span>
          <Select value={revId ? String(revId) : undefined} onValueChange={switchVersion}>
            <SelectTrigger className="h-9 w-48 text-sm" data-testid="sq-version-select"><SelectValue placeholder="Select version" /></SelectTrigger>
            <SelectContent>
              {revisions.map((r) => <SelectItem key={r.revision} value={String(r.revision)} data-testid={`sq-version-option-${r.revision}`}>Rev {r.revision}{r.label ? ` · ${r.label}` : ""}{r.active ? " (current)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={addRevision} disabled={creatingRev} data-testid="sq-add-revision-button">
            {creatingRev ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Add Revision
          </Button>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground" title="New revision also starts with an empty document list">
            <Checkbox checked={clearDocs} onCheckedChange={(v) => setClearDocs(!!v)} data-testid="sq-clear-docs" />Clear Docs
          </label>
          {revisions.length > 1 && (
            <Button variant="ghost" size="icon" onClick={deleteVersion} className="text-red-500 hover:bg-red-50 hover:text-red-600" title={`Delete Rev ${revId}`} data-testid="sq-delete-version">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="sq-save-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Save Quote
          </Button>
        </div>
      </div>

      <RFQWorkflow title="Quote Workflow" steps={wfSteps} currentIndex={wfCurrent} />

      <div className="space-y-4">
        {/* STEP 1 — Capture Quote */}
        <StepShell n={1} title="Capture Quote" subtitle="Paste or upload the supplier's quote, then Read with AI." active={isOpen(1)} done={captureDone} summary={step1Summary} onOpen={() => openStep(1)} onToggle={() => closeStep(1)}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
            <div className="flex flex-col rounded-lg border border-dashed border-border bg-white p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700"><Sparkles className="h-4 w-4 text-primary" /> Auto-fill from Quote Documents</div>
              <Textarea rows={4} placeholder="Paste the supplier's quote text (optional)..." value={text} onChange={(e) => setText(e.target.value)} data-testid="quote-text" />
              <label className={`mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-3 text-xs font-medium hover:bg-slate-100 ${docs.length ? "border-input bg-slate-50 text-slate-600" : "border-[#EAD9A0] bg-[#FFFBEB] text-[#8A6D1F]"}`} data-testid="quote-docs-add-label">
                <FileUp className="h-4 w-4" /> Add PDF / Excel / Image documents
                <input type="file" multiple accept="application/pdf,.xlsx,.xls,image/*" className="hidden" onChange={(e) => { addDocs(e.target.files); e.target.value = ""; }} data-testid="quote-docs-input" />
              </label>
              {docs.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="quote-docs-staged">
                  {docs.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs" data-testid={`quote-doc-staged-${i}`}>
                      <FileText className="h-3.5 w-3.5 shrink-0 text-[#1B4383]" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">{f.name}</span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">{docTypeOf(f)}</span>
                      <button onClick={() => removeDoc(i)} className="shrink-0 text-slate-400 hover:text-destructive" data-testid={`quote-doc-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs text-muted-foreground">Add one or more documents — a supplier may split their quote across files. Uploaded documents are also saved to Quote Documents.</p>
              <Button className="mt-3 self-start" size="sm" onClick={parse} disabled={parsing} data-testid="parse-quote-btn">
                {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{parsing ? "Reading..." : docs.length > 1 ? `Read ${docs.length} Quotes with AI` : "Read Quote with AI"}
              </Button>
            </div>

            <div className="flex flex-col rounded-lg border border-slate-200 bg-slate-100/70">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">Quote Documents &amp; Supporting Datasheets</h3>
                  <span className="text-[11px] text-muted-foreground">Reference only — the supplier's quote files, datasheets &amp; images on record</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50 ${files.length ? "border-input bg-white text-slate-700" : "border-[#EAD9A0] bg-[#FFFBEB] text-[#8A6D1F]"}`} data-testid="sq-upload-label">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Upload Quote
                    <input type="file" multiple className="hidden" onChange={uploadOnly} data-testid="sq-upload-input" />
                  </label>
                  <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50 ${files.length ? "border-input bg-white text-slate-700" : "border-[#EAD9A0] bg-[#FFFBEB] text-[#8A6D1F]"}`} data-testid="sq-datasheet-label">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Datasheet / Image
                    <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.xls,.xlsx,.doc,.docx" className="hidden" onChange={uploadDatasheets} data-testid="sq-datasheet-input" />
                  </label>
                </div>
              </div>
              <div className="flex-1 divide-y divide-border overflow-y-auto">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 px-4 py-3" data-testid={`sq-file-${f.id}`}>
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><FileText className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">{f.filename}</span>
                        {f.kind === "datasheet" && <span className="shrink-0 rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]">Datasheet</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{fmtBytes(f.size)} · {fmtWhen(f.at)}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}</div>
                    </div>
                    <button onClick={() => downloadFile(f)} className="text-slate-400 hover:text-primary" title="Download" data-testid={`sq-file-download-${f.id}`}><Download className="h-4 w-4" /></button>
                    <button onClick={() => deleteFile(f.id)} className="text-slate-400 hover:text-destructive" title="Delete" data-testid={`sq-file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {!files.length && <div className="px-4 py-8 text-center text-xs text-muted-foreground">No documents yet — upload the supplier's quote or supporting datasheets/images to keep them on file.</div>}
              </div>
            </div>
          </div>

          <WizardNav onNext={() => advance(1, 2)} nextLabel="Continue to Verify Prices" nextTestId="wizard-next-1" />
        </StepShell>

        {/* STEP 2 — Verify Prices */}
        <StepShell n={2} title="Verify Prices" subtitle="Confirm currency, line prices, terms & any extras." active={isOpen(2)} done={verifyDone} summary={step2Summary} onOpen={() => openStep(2)} onToggle={() => closeStep(2)}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Currency</Label>
                {meta.currency ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF1FB] px-2 py-0.5 text-[10px] font-semibold text-[#1B4383]" data-testid="currency-ai-badge"><Sparkles className="h-2.5 w-2.5" />AI Detected</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500" data-testid="currency-not-detected">Not detected yet — Read Quote with AI or pick manually</span>
                )}
              </div>
              <Select value={meta.currency || undefined} onValueChange={(v) => setMeta({ ...meta, currency: v })}>
                <SelectTrigger className={`${meta.currency ? "" : "ring-2 ring-amber-400 animate-pulse"}`} data-testid="quote-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                <SelectContent>{QUOTE_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Feeds the USD conversion in Costing.</p>
            </div>
            <div className="space-y-1"><Label className="text-xs">Payment Terms</Label><Input value={meta.payment_terms} onChange={(e) => setMeta({ ...meta, payment_terms: e.target.value })} placeholder="30 days net" className={`${meta.payment_terms ? "" : INPUT_YELLOW}`} data-testid="quote-terms" /></div>
            <div className="space-y-1"><Label className="text-xs">Validity</Label><Input value={meta.validity} onChange={(e) => setMeta({ ...meta, validity: e.target.value })} placeholder="30 days" className={`${meta.validity ? "" : INPUT_YELLOW}`} data-testid="quote-validity" /></div>
          </div>

          <div className="rounded-lg border border-border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
              <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">Line Prices</h3>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-muted-foreground" data-testid="sq-mapped-hint">{mappedCount} of {lines.length} Line(s) Mapped</span>
                <div className="flex items-center gap-1.5" title="Sets the same supplier discount on every line">
                  <span className="text-xs text-muted-foreground">Apply Disc % To All</span>
                  <Input inputMode="decimal" placeholder="%" className="h-8 w-16 text-right font-mono" onKeyDown={(e) => { if (e.key === "Enter") { applyDiscAll(e.currentTarget.value.replace(/[^0-9.]/g, "")); } }}
                    onBlur={(e) => { if (e.currentTarget.value !== "") applyDiscAll(e.currentTarget.value.replace(/[^0-9.]/g, "")); }} disabled={!lines.length} data-testid="disc-apply-all" />
                </div>
                <Button size="sm" variant="outline" onClick={autoMatch} disabled={matching || !lines.length} data-testid="auto-match-btn">
                  {matching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Auto-match
                </Button>
              </div>
            </div>
            <div className="hidden overflow-x-auto sm:block">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 font-medium">Quote Line</th>
                    <th className="px-4 py-2.5 font-medium">Matched Item</th>
                    <th className="px-4 py-2.5 font-medium text-right">Req. Qty</th>
                    <th className="whitespace-nowrap px-4 py-2.5 font-medium text-right">Qty Quoted</th>
                    <th className="px-4 py-2.5 font-medium">Unit Price</th>
                    <th className="px-4 py-2.5 font-medium">Disc %</th>
                    <th className="px-4 py-2.5 font-medium text-right">Total Price</th>
                    <th className="px-4 py-2.5 font-medium">Lead Time</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-border align-top" data-testid={`quote-line-${i}`}>
                      <td className="px-4 py-3 text-xs text-muted-foreground w-[260px]">
                        <div className="whitespace-normal break-words font-medium text-slate-700">{l.description || "—"}</div>
                        {l.part_number && <div className="font-mono text-[10px]">{l.part_number}</div>}
                      </td>
                      <td className="px-4 py-3 min-w-[220px]">
                        <Select value={l.item_id || "none"} onValueChange={(v) => setLine(i, { item_id: v === "none" ? "" : v })}>
                          <SelectTrigger className={`h-auto min-h-9 whitespace-normal py-1.5 text-xs [&>span]:line-clamp-none ${!l.item_id ? INPUT_YELLOW : ""}`} data-testid={`quote-map-${i}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Ignore this line —</SelectItem>
                            {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.description || "(untitled)"}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 w-24 text-right" data-testid={`quote-reqqty-${i}`}>
                        {l.item_id ? (
                          <span className="font-mono text-sm text-slate-700">{itemQty(l.item_id) != null ? itemQty(l.item_id) : "—"}{itemUnit(l.item_id) ? <span className="ml-1 text-[11px] text-muted-foreground">{itemUnit(l.item_id)}</span> : null}</span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 w-28">{QtyInput({ l, i })}{qtyDiff(l) && <div className="mt-1 whitespace-nowrap text-right text-[10px] font-medium text-amber-700" data-testid={`quote-qty-diff-${i}`}>≠ Request</div>}</td>
                      <td className="px-4 py-3 w-36"><Input inputMode="decimal" value={withCommas(l.unit_price)} onChange={(e) => setLine(i, { unit_price: e.target.value.replace(/[^0-9.]/g, "") })} className={`h-9 text-right font-mono ${INPUT_YELLOW} ${l.item_id && (l.unit_price === "" || l.unit_price == null) ? "ring-2 ring-amber-400 animate-pulse" : ""}`} data-testid={`quote-price-${i}`} /></td>
                      <td className="px-4 py-3 w-20"><Input inputMode="decimal" placeholder="0" value={l.disc_pct ?? ""} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setLine(i, { disc_pct: v !== "" && Number(v) > 100 ? "100" : v }); }} className="h-9 text-right font-mono" data-testid={`quote-disc-${i}`} /></td>
                      <td className="px-4 py-3 w-32 text-right font-mono text-xs text-slate-700" data-testid={`quote-total-${i}`}>{fmtTotal(lineTotal(l))}{Number(l.disc_pct) > 0 && listOf(l) != null ? <div className="text-[10px] text-emerald-700">Net {withCommas(netOf(l).toFixed(2))} / Unit</div> : null}</td>
                      <td className="px-4 py-3 w-64"><Input value={l.lead_time} onChange={(e) => setLine(i, { lead_time: e.target.value })} placeholder="e.g. 4–6 weeks ex-works" className={`h-9 ${INPUT_YELLOW}`} data-testid={`quote-lead-${i}`} /></td>
                      <td className="px-4 py-3 text-right"><button onClick={() => removeLine(i)} className="text-slate-400 hover:text-destructive" data-testid={`quote-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                  {!lines.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground text-xs">No line items yet — capture a quote in Step 1, or this supplier has no assigned items.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="divide-y divide-border sm:hidden">
              {lines.map((l, i) => (
                <div key={i} className="space-y-3 p-4" data-testid={`quote-line-m-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="break-words text-sm font-medium text-slate-700">{l.description || "—"}</div>
                      {l.part_number && <div className="font-mono text-[10px] text-muted-foreground">{l.part_number}</div>}
                    </div>
                    <button onClick={() => removeLine(i)} className="shrink-0 text-slate-400 hover:text-destructive" data-testid={`quote-remove-m-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Matched Item</Label>
                    <Select value={l.item_id || "none"} onValueChange={(v) => setLine(i, { item_id: v === "none" ? "" : v })}>
                      <SelectTrigger className={`h-auto min-h-9 whitespace-normal py-1.5 text-xs [&>span]:line-clamp-none ${!l.item_id ? INPUT_YELLOW : ""}`} data-testid={`quote-map-m-${i}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Ignore this line —</SelectItem>
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.description || "(untitled)"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Req. Qty</Label>
                      <div className="flex h-9 items-center font-mono text-sm text-slate-700" data-testid={`quote-reqqty-m-${i}`}>{l.item_id ? (itemQty(l.item_id) != null ? `${itemQty(l.item_id)}${itemUnit(l.item_id) ? " " + itemUnit(l.item_id) : ""}` : "—") : "—"}</div>
                      <Label className="text-[11px] text-muted-foreground">Qty Quoted</Label>
                      {QtyInput({ l, i, m: "-m" })}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Unit Price</Label>
                      <Input inputMode="decimal" value={withCommas(l.unit_price)} onChange={(e) => setLine(i, { unit_price: e.target.value.replace(/[^0-9.]/g, "") })} className={`h-9 text-right font-mono ${INPUT_YELLOW} ${l.item_id && (l.unit_price === "" || l.unit_price == null) ? "ring-2 ring-amber-400 animate-pulse" : ""}`} data-testid={`quote-price-m-${i}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Disc %</Label>
                      <Input inputMode="decimal" placeholder="0" value={l.disc_pct ?? ""} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setLine(i, { disc_pct: v !== "" && Number(v) > 100 ? "100" : v }); }} className="h-9 text-right font-mono" data-testid={`quote-disc-m-${i}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Total</Label>
                      <div className="flex h-9 items-center font-mono text-xs text-slate-700" data-testid={`quote-total-m-${i}`}>{fmtTotal(lineTotal(l))}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Lead Time</Label>
                      <Input value={l.lead_time} onChange={(e) => setLine(i, { lead_time: e.target.value })} placeholder="e.g. 4–6 weeks" className={`h-9 ${INPUT_YELLOW}`} data-testid={`quote-lead-m-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
              {!lines.length && <div className="px-4 py-10 text-center text-xs text-muted-foreground">No line items yet — capture a quote in Step 1, or this supplier has no assigned items.</div>}
            </div>
            {discSum > 0.005 && (
              <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 border-t border-border bg-emerald-50/50 px-4 py-2.5 font-mono text-xs" data-testid="sq-discount-summary">
                <span className="text-slate-500">Subtotal {fmtTotal(grossSum)}</span>
                <span className="text-emerald-700">Less Supplier Discount −{fmtTotal(discSum)}</span>
                <span className="font-semibold text-slate-800">Net {fmtTotal(matchedSum)} · Used In Costing</span>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div>
                <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">Extra & Alternative Lines</h3>
                <p className="text-xs text-muted-foreground">Add-ons the supplier bundles in (e.g. a connector for a hose) or alternative options they offer.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => addExtra("addon")} data-testid="add-addon-btn"><Plus className="mr-1.5 h-4 w-4" />Add-on</Button>
                <Button size="sm" variant="outline" onClick={() => addExtra("alternative")} data-testid="add-alt-btn"><Puzzle className="mr-1.5 h-4 w-4" />Alternative</Button>
              </div>
            </div>
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">Applies To</th>
                    <th className="px-3 py-2.5 font-medium">Description</th>
                    <th className="px-3 py-2.5 font-medium">Qty</th>
                    <th className="px-3 py-2.5 font-medium">Unit Price</th>
                    <th className="px-3 py-2.5 font-medium text-right">Total</th>
                    <th className="px-3 py-2.5 font-medium">Lead Time</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {extras.map((e, i) => (
                    <tr key={i} className="border-t border-border align-top" data-testid={`extra-row-${i}`}>
                      <td className="px-3 py-2.5 w-32">
                        <Select value={e.kind} onValueChange={(v) => setExtra(i, { kind: v, parent_item_id: v === "alternative" && !e.parent_item_id ? "" : e.parent_item_id })}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`extra-kind-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="addon">Add-on</SelectItem>
                            <SelectItem value="alternative">Alternative</SelectItem>
                          </SelectContent>
                        </Select>
                        {e.kind === "addon" && (
                          <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Checkbox checked={e.optional} onCheckedChange={(v) => setExtra(i, { optional: !!v })} data-testid={`extra-optional-${i}`} /> Optional
                          </label>
                        )}
                      </td>
                      <td className="px-3 py-2.5 min-w-[190px]">
                        <Select value={e.parent_item_id || (e.kind === "addon" ? "standalone" : "")} onValueChange={(v) => setExtra(i, { parent_item_id: v === "standalone" ? "" : v })}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`extra-parent-${i}`}><SelectValue placeholder={e.kind === "alternative" ? "Replaces which item?" : "Attach to..."} /></SelectTrigger>
                          <SelectContent>
                            {e.kind === "addon" && <SelectItem value="standalone">— Standalone (freight, etc.) —</SelectItem>}
                            {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.description || "(untitled)"}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2.5 min-w-[200px]"><Input value={e.description} onChange={(ev) => setExtra(i, { description: ev.target.value })} placeholder="e.g. Hose connector G3/4" className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-desc-${i}`} /></td>
                      <td className="px-3 py-2.5 w-20"><Input type="number" value={e.quantity} onChange={(ev) => setExtra(i, { quantity: ev.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-qty-${i}`} /></td>
                      <td className="px-3 py-2.5 w-24"><Input type="number" value={e.unit_price} onChange={(ev) => setExtra(i, { unit_price: ev.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-price-${i}`} /></td>
                      <td className="px-3 py-2.5 w-32 text-right font-mono text-xs text-slate-700" data-testid={`extra-total-${i}`}>{fmtTotal(extraTotal(e))}</td>
                      <td className="px-3 py-2.5 w-52"><Input value={e.lead_time} onChange={(ev) => setExtra(i, { lead_time: ev.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-lead-${i}`} /></td>
                      <td className="px-3 py-2.5 text-right"><button onClick={() => removeExtra(i)} className="text-slate-400 hover:text-destructive" data-testid={`extra-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                  {!extras.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-xs">No extras. Use “Add-on” for bundled/required items or “Alternative” for optional substitutes.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="divide-y divide-border sm:hidden">
              {extras.map((e, i) => (
                <div key={i} className="space-y-3 p-4" data-testid={`extra-row-m-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.kind === "alternative" ? "bg-violet-100 text-violet-700" : "bg-[#E6ECF5] text-[#1B4383]"}`}>{e.kind === "alternative" ? "Alternative" : "Add-on"}</span>
                      <span className="font-mono text-xs text-slate-700" data-testid={`extra-total-m-${i}`}>{fmtTotal(extraTotal(e))}</span>
                    </div>
                    <button onClick={() => removeExtra(i)} className="text-slate-400 hover:text-destructive" data-testid={`extra-remove-m-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Type</Label>
                      <Select value={e.kind} onValueChange={(v) => setExtra(i, { kind: v, parent_item_id: v === "alternative" && !e.parent_item_id ? "" : e.parent_item_id })}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`extra-kind-m-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="addon">Add-on</SelectItem>
                          <SelectItem value="alternative">Alternative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">{e.kind === "alternative" ? "Replaces" : "Applies To"}</Label>
                      <Select value={e.parent_item_id || (e.kind === "addon" ? "standalone" : "")} onValueChange={(v) => setExtra(i, { parent_item_id: v === "standalone" ? "" : v })}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`extra-parent-m-${i}`}><SelectValue placeholder={e.kind === "alternative" ? "Which item?" : "Attach to..."} /></SelectTrigger>
                        <SelectContent>
                          {e.kind === "addon" && <SelectItem value="standalone">— Standalone (freight, etc.) —</SelectItem>}
                          {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.description || "(untitled)"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {e.kind === "addon" && (
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Checkbox checked={e.optional} onCheckedChange={(v) => setExtra(i, { optional: !!v })} data-testid={`extra-optional-m-${i}`} /> Optional
                    </label>
                  )}
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Description</Label>
                    <Input value={e.description} onChange={(ev) => setExtra(i, { description: ev.target.value })} placeholder="e.g. Hose connector G3/4" className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-desc-m-${i}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Qty</Label>
                      <Input type="number" value={e.quantity} onChange={(ev) => setExtra(i, { quantity: ev.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-qty-m-${i}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Unit Price</Label>
                      <Input type="number" value={e.unit_price} onChange={(ev) => setExtra(i, { unit_price: ev.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-price-m-${i}`} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Lead Time</Label>
                    <Input value={e.lead_time} onChange={(ev) => setExtra(i, { lead_time: ev.target.value })} placeholder="e.g. 4–6 weeks" className={`h-9 ${INPUT_YELLOW}`} data-testid={`extra-lead-m-${i}`} />
                  </div>
                </div>
              ))}
              {!extras.length && <div className="px-3 py-8 text-center text-xs text-muted-foreground">No extras. Use “Add-on” for bundled/required items or “Alternative” for optional substitutes.</div>}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-[#F7F9FC] px-5 py-4" data-testid="sq-totals">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-6"><span className="text-muted-foreground">Line Items</span><span className="font-mono text-slate-700">{fmtTotal(matchedSum)}</span></div>
                <div className="flex items-center gap-6"><span className="text-muted-foreground">Add-Ons</span><span className="font-mono text-slate-700">{fmtTotal(addonSum)}</span></div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Quote Total</div>
                <div className="font-heading text-2xl font-bold text-slate-900" data-testid="sq-quote-total">{fmtTotal(baseTotal)}</div>
                {altCount > 0 && <div className="mt-1 text-xs text-muted-foreground">+ {altCount} alternative option{altCount !== 1 ? "s" : ""} (decide at Compare)</div>}
              </div>
            </div>
          </div>

          <WizardNav onBack={() => advance(2, 1)} onNext={goToCosting} nextLabel="Save & Continue to Costing" nextTestId="wizard-next-2" nextLoading={advancing} />
        </StepShell>

        {/* STEP 3 — Build Costing */}
        {!rfq?.is_stock && <StepShell n={3} title="Build Costing" subtitle="Freight, duties & FX → landed cost + selling price." active={isOpen(3)} done={costingDone} summary={step3Summary} onOpen={() => openStep(3)} onToggle={() => closeStep(3)}>
          <div data-testid="sq-costing-step">
            <CostingSection key={`cost-${revId}-${costingReloadKey}`} rfqId={id} supplierId={supplierId} currency={meta.currency} onStatus={(st) => setCostingDone(!!st.basisConfirmed)} />
          </div>
          <WizardNav onBack={() => advance(3, 2)} />
        </StepShell>}
      </div>
    </AppLayout>
  );
}
